import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../utils/axios";

interface LevelItem {
  level: number;
  total_bet_amt: number;
}

const Level = () => {
  const [levels, setLevels] = useState<LevelItem[]>([]);
  const [editingLevel, setEditingLevel] = useState<LevelItem | null>(null);
  const [newLevel, setNewLevel] = useState<LevelItem>({
    level: 0,
    total_bet_amt: 0,
  });

  /* ================= FETCH ================= */
  const fetchLevels = async () => {
    try {
      const res = await api.get("/getLevel");
      setLevels(res.data.data || []);
    } catch {
      toast.error("Failed to fetch levels");
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  /* ================= ADD ================= */
  const handleAddLevel = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newLevel.level || !newLevel.total_bet_amt) {
      toast.warning("Please fill all fields");
      return;
    }

    try {
      await api.post("/createLevel", newLevel);
      toast.success("Level added successfully");
      setNewLevel({ level: 0, total_bet_amt: 0 });
      fetchLevels();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error adding level");
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdateLevel = async () => {
    if (!editingLevel) return;

    try {
      await api.post(`/updateLevel/${editingLevel.level}`, editingLevel);
      toast.success("Level updated");
      setEditingLevel(null);
      fetchLevels();
    } catch {
      toast.error("Failed to update level");
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (level: number) => {
    if (!window.confirm("Delete this level?")) return;

    try {
      await api.delete(`/deleteLevel/${level}`);
      toast.success("Level deleted");
      fetchLevels();
    } catch {
      toast.error("Failed to delete level");
    }
  };

  return (
    <div className="space-y-8">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Level Management
        </h1>
        <p className="text-sm text-gray-500">
          Configure user levels based on total bet amount
        </p>
      </div>

      {/* ================= ADD LEVEL ================= */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Add New Level</h2>

        <form
          onSubmit={handleAddLevel}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <input
            type="number"
            placeholder="Level"
            value={newLevel.level}
            onChange={(e) =>
              setNewLevel({ ...newLevel, level: +e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            placeholder="Total Bet Amount"
            value={newLevel.total_bet_amt}
            onChange={(e) =>
              setNewLevel({
                ...newLevel,
                total_bet_amt: +e.target.value,
              })
            }
            className="border rounded-lg px-3 py-2"
          />

          <button
            type="submit"
            className="bg-gray-900 text-white rounded-lg px-4 py-2 hover:bg-gray-800"
          >
            Add Level
          </button>
        </form>
      </div>

      {/* ================= LEVEL TABLE ================= */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 text-left">SNo.</th>
               <th className="px-4 py-3 text-left">Level</th>
              <th className="px-4 py-3 text-left">Total Bet Amount</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {levels.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="py-8 text-center text-gray-500"
                >
                  No levels found
                </td>
              </tr>
            ) : (
              levels.map((l, i) => (
                <tr
                  key={l.level}
                  className="border-t hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-medium">
                    {i + 1}
                  </td>
                  <td className="px-4 py-3">
                    Level {l.level}
                  </td>

                  <td className="px-4 py-3">
                    {editingLevel?.level === l.level ? (
                      <input
                        type="number"
                        value={editingLevel.total_bet_amt}
                        onChange={(e) =>
                          setEditingLevel({
                            ...editingLevel,
                            total_bet_amt: +e.target.value,
                          })
                        }
                        className="border rounded px-2 py-1 w-32"
                      />
                    ) : (
                      l.total_bet_amt
                    )}
                  </td>

                  <td className="px-4 py-3 flex gap-2">
                    {editingLevel?.level === l.level ? (
                      <>
                        <button
                          onClick={handleUpdateLevel}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingLevel(null)}
                          className="px-3 py-1 border rounded text-xs"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingLevel(l)}
                          className="px-3 py-1 bg-yellow-500 text-white rounded text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(l.level)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-xs"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Level;
